class CartRewardsBar extends HTMLElement {
  constructor() {
    super();
    this.tiers = [];
    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]')
        .map((t) => ({ ...t, threshold: parseFloat(t.threshold) || 0 }))
        .sort((a, b) => a.threshold - b.threshold);
    } catch (e) {
      console.error('cart-rewards-bar: invalid tiers JSON', e);
    }
    this.currency = this.dataset.currency || 'INR';
    this.headingEl = this.querySelector('.cart-rewards-bar__heading');
    this.fillEl = this.querySelector('.cart-rewards-bar__fill');
    this.dotsEl = this.querySelector('.cart-rewards-bar__dots');
    this.legendEl = this.querySelector('.cart-rewards-bar__legend');
    this.messageEl = this.querySelector('.cart-rewards-bar__message');
    if (this.headingEl) this.headingEl.textContent = window.cartRewardsHeading || this.headingEl.textContent;
    this.lastUnlockedCount = 0;
    this.render();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const total = event?.cartData?.total_price;
      if (typeof total === 'number') this.updateTotal(total, true);
    });
    this.updateTotal(parseInt(this.dataset.cartTotal, 10) || 0, false);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  get finalThreshold() {
    return this.tiers.length ? this.tiers[this.tiers.length - 1].threshold : 0;
  }

  formatMoney(rupees) {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.currency, maximumFractionDigits: 0 }).format(rupees);
    } catch (e) {
      return `₹${Math.round(rupees)}`;
    }
  }

  // Build static dots (positions only) + legend pills once
  render() {
    if (!this.tiers.length) return;
    const finalThreshold = this.finalThreshold;

    this.dotsEl.innerHTML = this.tiers
      .map((tier, i) => {
        const pos = finalThreshold ? Math.min((tier.threshold / finalThreshold) * 100, 100) : 0;
        return `<span class="cart-rewards-bar__dot" data-tier-index="${i}" style="left:${pos}%;"></span>`;
      })
      .join('');

    this.legendEl.innerHTML = this.tiers
      .map((tier, i) => {
        return `<span class="cart-rewards-bar__pill" data-tier-index="${i}">
          <span class="cart-rewards-bar__pill-check">✓</span>
          <span class="cart-rewards-bar__pill-text">${tier.label || `₹${tier.threshold}+`}</span>
        </span>`;
      })
      .join('');
  }

  updateTotal(totalCents, animate) {
    const totalRupees = (totalCents || 0) / 100;
    const finalThreshold = this.finalThreshold;
    const progress = finalThreshold ? Math.min((totalRupees / finalThreshold) * 100, 100) : 0;

    if (this.fillEl) {
      this.fillEl.style.width = `${progress}%`;
      this.fillEl.classList.add('is-animating');
    }

    let currentTier = null;
    let nextTier = null;
    let unlockedCount = 0;
    this.tiers.forEach((tier) => {
      if (totalRupees >= tier.threshold) {
        currentTier = tier;
        unlockedCount += 1;
      } else if (!nextTier) {
        nextTier = tier;
      }
    });

    this.dotsEl?.querySelectorAll('.cart-rewards-bar__dot').forEach((dot, i) => {
      const unlocked = totalRupees >= this.tiers[i].threshold;
      dot.classList.toggle('is-unlocked', unlocked);
    });

    this.legendEl?.querySelectorAll('.cart-rewards-bar__pill').forEach((pill, i) => {
      const unlocked = totalRupees >= this.tiers[i].threshold;
      pill.classList.toggle('is-unlocked', unlocked);
    });

    // Pulse animation only when a NEW tier just got unlocked
    if (animate && unlockedCount > this.lastUnlockedCount) {
      this.classList.add('is-celebrating');
      setTimeout(() => this.classList.remove('is-celebrating'), 900);
    }
    this.lastUnlockedCount = unlockedCount;

    if (this.messageEl) {
      if (nextTier) {
        const remaining = Math.max(nextTier.threshold - totalRupees, 0);
        this.messageEl.innerHTML = `Add <strong>${this.formatMoney(remaining)}</strong> more to unlock: ${nextTier.label}`;
      } else if (currentTier) {
        this.messageEl.innerHTML = `🎉 All rewards unlocked: ${currentTier.label}`;
      } else {
        this.messageEl.innerHTML = '';
      }
    }

    this.renderCodeChip(currentTier);
    this.handleFreeGift(currentTier);
  }

  renderCodeChip(currentTier) {
    let chip = this.querySelector('.cart-rewards-bar__code-chip');
    const hasCode = currentTier && (currentTier.type === 'discount_code' || currentTier.type === 'discount_percent') && currentTier.value;
    if (hasCode) {
      if (!chip) {
        chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'cart-rewards-bar__code-chip';
        this.appendChild(chip);
        chip.addEventListener('click', () => {
          navigator.clipboard?.writeText(chip.dataset.code || '');
          chip.classList.add('is-copied');
          setTimeout(() => chip.classList.remove('is-copied'), 1500);
        });
      }
      chip.dataset.code = currentTier.value;
      chip.textContent = chip.classList.contains('is-copied') ? 'Copied!' : `Code: ${currentTier.value}`;
    } else if (chip) {
      chip.remove();
    }
  }

  async handleFreeGift(currentTier) {
    if (!currentTier || currentTier.type !== 'free_gift' || !currentTier.value) return;
    if (this._lastGiftVariant === currentTier.value) return;
    try {
      await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: currentTier.value, quantity: 1, properties: { _free_gift: 'true' } }] }),
      });
      this._lastGiftVariant = currentTier.value;
    } catch (e) {
      console.error('cart-rewards-bar: failed to add free gift', e);
    }
  }
}

if (!customElements.get('cart-rewards-bar')) {
  customElements.define('cart-rewards-bar', CartRewardsBar);
}